import React from 'react';
import {MobileDrawer} from '../mobile/MobileDrawer';
import {TaskModal} from '../topics/tasks/TaskModal';
import DashboardHeader from './DashboardHeader';
import DashboardSkeleton from './DashboardSkeleton';
import {MobileDashboardView} from './mobile/MobileDashboardView';
import MobileDashboardSkeleton from './mobile/MobileDashboardSkeleton';
import {ProjectFilter, TaskItem, UserFilter} from './types/dashboardTypes';

// Import drag and drop related components and hooks
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import DroppableDashboardColumn from './dnd/DroppableDashboardColumn';
import DatePickerModal from './dnd/DatePickerModal';
import {AlertTriangle, Clock, Star} from 'lucide-react';
import DashboardTaskCard from './DashboardTaskCard';

/**
 * A stateless version of the dashboard with drag and drop functionality
 * This component receives all state and handlers as props to avoid hooks ordering issues
 */
interface DashboardWithDndStatelessProps {
  // Viewport and UI state
  isMobile: boolean;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  isFilterDropdownOpen: boolean;
  setIsFilterDropdownOpen: (open: boolean) => void;

  // Task data
  isLoading: boolean;
  overdueTasks: TaskItem[];
  todayTasks: TaskItem[];
  thisWeekTasks: TaskItem[];
  highlightedTasks: TaskItem[];

  // Project and user filters
  projects: Array<{ id: string; name: string; isShared?: boolean }>;
  projectFilters: ProjectFilter[];
  toggleProjectFilter: (projectId: string, isExclusive?: boolean) => void;
  userFilters: UserFilter[];
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
  activeUserIds?: string[];

  // Tag filters
  availableTags: string[];
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;

  // Task actions
  editingTask: TaskItem | null;
  setEditingTask: (task: TaskItem | null) => void;
  handleUpdateTask: (taskId: string, updates: Partial<TaskItem>) => Promise<void>;
  handleDeleteTask: (taskId: string) => Promise<void>;
  navigateToTask: (task: TaskItem) => void;

  // Drag and drop state
  activeTaskId: string | null;
  activeTask: TaskItem | null;
  showDatePicker: boolean;
  setShowDatePicker: (show: boolean) => void;
  taskForDatePicker: TaskItem | null;

  // Drag and drop handlers
  handleDragStart: (event: any) => void;
  handleDragOver: (event: any) => void;
  handleDragEnd: (event: any) => void;
  handleDateSelection: (date: Date | null) => void;
  resetDragState: () => void;
}

const DashboardDesktopView: React.FC<DashboardWithDndStatelessProps> = ({
  // Viewport and UI state
  isMobile,
  isDrawerOpen,
  setIsDrawerOpen,
  isFilterDropdownOpen,
  setIsFilterDropdownOpen,

  // Task data
  isLoading,
  overdueTasks,
  todayTasks,
  thisWeekTasks,
  highlightedTasks,

  // Project and user filters
  projects,
  projectFilters,
  toggleProjectFilter,
  userFilters,
  toggleUserFilter,
  activeUserIds,

  // Tag filters
  availableTags,
  selectedTags,
  setSelectedTags,

  // Task actions
  editingTask,
  setEditingTask,
  handleUpdateTask,
  handleDeleteTask,
  navigateToTask,

  // Drag and drop state
  activeTaskId,
  activeTask,
  showDatePicker,
  setShowDatePicker,
  taskForDatePicker,

  // Drag and drop handlers
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  handleDateSelection,
  resetDragState,
}) => {
  // Configure DND sensors - simplified for basic drag and drop, no sorting
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require an 8px movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: coordinates => coordinates, // Simple coordinates function
    })
  );

  // Enhanced loading state with different skeletons for mobile and desktop
  if (isLoading) {
    return isMobile ? <MobileDashboardSkeleton /> : <DashboardSkeleton />;
  }

  return (
    <div
      className={`h-full ${isMobile ? 'overflow-y-auto' : 'overflow-hidden'} bg-[var(--content-bg-color)] flex flex-col dashboard`}
    >
      {/* Mobile Drawer */}
      {isMobile && (
        <MobileDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} projectId="" />
      )}

      {/* Task Edit Modal */}
      {editingTask && (
        <TaskModal
          task={editingTask}
          projectId={(editingTask.topics as { project_id: string })?.project_id}
          onClose={() => setEditingTask(null)}
          onUpdateTask={handleUpdateTask}
          hasCollaborators={
            projects.find(p => p.id === (editingTask.topics as { project_id: string })?.project_id)
              ?.isShared ?? false
          }
        />
      )}

      {/* Date Picker Modal for selecting a date when dropping on This Week */}
      <DatePickerModal
        isOpen={showDatePicker}
        task={taskForDatePicker}
        onClose={() => setShowDatePicker(false)}
        onDateSelect={handleDateSelection}
      />

      {/* Dashboard Header - show on desktop, not on mobile */}
      {!isMobile && (
        <DashboardHeader
          isMobile={isMobile}
          projectFilters={projectFilters}
          userFilters={userFilters}
          isFilterDropdownOpen={isFilterDropdownOpen}
          setIsFilterDropdownOpen={setIsFilterDropdownOpen}
          toggleProjectFilter={toggleProjectFilter}
          toggleUserFilter={toggleUserFilter}
          setIsDrawerOpen={setIsDrawerOpen}
          availableTags={availableTags}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
          activeUserIds={activeUserIds}
        />
      )}

      {/* Task columns - desktop view */}
      {!isMobile ? (
        <div className="flex-1 overflow-hidden p-4 w-full mb-4">
          <div className="w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={args => {
                // First try with pointerWithin (most accurate for user interaction)
                const pointerCollisions = pointerWithin(args);
                if (pointerCollisions.length) {
                  return pointerCollisions;
                }

                // Then try rectIntersection (most generous)
                const rectCollisions = rectIntersection(args);
                if (rectCollisions.length) {
                  return rectCollisions;
                }

                // Finally, fall back to closestCenter (best compromise)
                return closestCenter(args);
              }}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={resetDragState}
              autoScroll={true}
            >
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
                data-dashboard-columns="true"
              >
                {/* Highlighted tasks - moved to first position */}
                <DroppableDashboardColumn
                  title="Highlighted"
                  icon={Star}
                  iconBgColor="bg-yellow-100"
                  iconColor="text-yellow-500"
                  tasks={highlightedTasks}
                  sectionType="highlighted"
                  projects={projects}
                  isMobile={isMobile}
                  onEditTask={setEditingTask}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                  onNavigateToTask={navigateToTask}
                  activeTaskId={activeTaskId}
                />

                {/* Overdue tasks */}
                <DroppableDashboardColumn
                  title="Overdue"
                  icon={AlertTriangle}
                  iconBgColor="bg-red-100"
                  iconColor="text-red-500"
                  tasks={overdueTasks}
                  sectionType="overdue"
                  projects={projects}
                  isMobile={isMobile}
                  onEditTask={setEditingTask}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                  onNavigateToTask={navigateToTask}
                  activeTaskId={activeTaskId}
                />

                {/* Today's tasks */}
                <DroppableDashboardColumn
                  title="Today"
                  icon={Clock}
                  iconBgColor="bg-green-100"
                  iconColor="text-green-500"
                  tasks={todayTasks}
                  sectionType="today"
                  projects={projects}
                  isMobile={isMobile}
                  onEditTask={setEditingTask}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                  onNavigateToTask={navigateToTask}
                  activeTaskId={activeTaskId}
                />

                {/* This week's tasks */}
                <DroppableDashboardColumn
                  title="This Week"
                  icon={Clock}
                  iconBgColor="bg-blue-100"
                  iconColor="text-blue-500"
                  tasks={thisWeekTasks}
                  sectionType="thisWeek"
                  projects={projects}
                  isMobile={isMobile}
                  onEditTask={setEditingTask}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                  onNavigateToTask={navigateToTask}
                  activeTaskId={activeTaskId}
                />
              </div>

              {/* Drag Overlay - shows a preview of the task being dragged */}
              <DragOverlay adjustScale={false} zIndex={100}>
                {activeTaskId && activeTask && (
                  <div
                    style={{
                      cursor: 'grabbing',
                      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.25)',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      transform: 'rotate(2deg)',
                      width: 'calc(100% - 48px)',
                      maxWidth: '24rem',
                    }}
                  >
                    <DashboardTaskCard
                      task={activeTask}
                      sectionType="today" // Default section type for overlay
                      projects={projects}
                      onEditTask={setEditingTask}
                      onUpdateTask={handleUpdateTask}
                      onDeleteTask={handleDeleteTask}
                      onNavigateToTask={navigateToTask}
                    />
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      ) : (
        /* Mobile dashboard view with column switcher */
        <div className="flex-1 w-full">
          <MobileDashboardView
            overdueTasks={overdueTasks}
            todayTasks={todayTasks}
            thisWeekTasks={thisWeekTasks}
            highlightedTasks={highlightedTasks}
            projects={projects}
            onEditTask={setEditingTask}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            onNavigateToTask={navigateToTask}
            setIsDrawerOpen={setIsDrawerOpen}
            projectFilters={projectFilters}
            userFilters={userFilters}
            toggleProjectFilter={toggleProjectFilter}
            toggleUserFilter={toggleUserFilter}
            availableTags={availableTags}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardDesktopView;
